import { useWindowListener } from '@e2m-customer-portal/hooks';
import { Button, DialogContent } from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';
import { Dialog, DialogProps } from '../dialog/Dialog';

export interface AbortConfirmationDialogProps {
  when: boolean;
  title?: string;
  message?: string;
}

export function AbortConfirmationDialog({
  when,
  title,
  message,
}: Readonly<AbortConfirmationDialogProps>): ReactElement<AbortConfirmationDialogProps> {
  const [t] = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => when && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsOpen(true);
    }
  }, [blocker.state]);

  const handleOkButtonClick = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
    setIsOpen(false);
  }, [blocker]);

  const handleCancelButtonClick = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    setIsOpen(false);
  }, [blocker]);

  const handleDialogClose = useCallback<NonNullable<DialogProps['onClose']>>(
    (_e, reason) => {
      if (reason === 'escapeKeyDown' || reason === 'cancelClick') {
        if (blocker.state === 'blocked') {
          blocker.reset();
        }
        setIsOpen(false);
      }
    },
    [blocker],
  );

  useWindowListener(
    'beforeunload',
    (event) => {
      if (when) {
        event.preventDefault();
        // For security reasons, browsers do not show a custom message and do not allow a custom confirmation dialog.
        // However, some browsers may still only trigger the dialog if a message string is returned here.
        event.returnValue = '';
        return '';
      }
    },
    {},
    [when],
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      title={title ?? t('SHARED_ABORT_TITLE')}
      actions={
        <>
          <Button role="button" variant="outlined" onClick={handleCancelButtonClick}>
            {t('SHARED_CANCEL')}
          </Button>
          <Button role="button" onClick={handleOkButtonClick}>
            {t('SHARED_DISCARD')}
          </Button>
        </>
      }
    >
      <DialogContent>{message ?? t('SHARED_ABORT_MESSAGE')}</DialogContent>
    </Dialog>
  );
}
